import { gql } from "@apollo/client/core";
import { assetFragment, assetService, materialFragment } from "./asset";
import promiseCache from "./promiseCache";
import { graphQlManager } from "./server";
import { OptionResource, VariantResource } from "../types";

export const optionFragment = gql`
    fragment OptionFields on Option {
        id
        name
        public
        currencyCode
        type
        workflowId
        displayType
        integrationOptions {
            id
            integrationId
        }
        variants {
            id
            name
            enabled
            color
            namedColor
            priceModifier
            asset {
                ...AssetFields
            }
            thumbnail {
                ...AssetFields
            }
            material {
                ...MaterialFields
            }
        }
        defaultVariant {
            id
            name
            asset {
                ...AssetFields
            }
        }
        colorProfile {
            ...AssetFields
        }
    }
`;

const getOptionsQuery = gql`
    ${assetFragment(false)}
    ${materialFragment}
    ${optionFragment}
    query GetOptions($ids: [String]!) {
        options(ids: $ids) {
            ...OptionFields
        }
    }
`;

const getOptionsInternal = async (ids: string[]): Promise<OptionResource[]> => {
    const response = await graphQlManager.getShadowGraphqlClient().query<{ options: OptionResource[] }>({
        query: getOptionsQuery,
        errorPolicy: "all",
        variables: {
            ids,
        },
    });
    response.data.options.forEach((option) => {
        option.defaultVariant?.asset && assetService.cacheAsset(option.defaultVariant.asset);
        option.colorProfile && assetService.cacheAsset(option.colorProfile);
        option.variants?.forEach((variant) => {
            variant.asset && assetService.cacheAsset(variant.asset);
            variant.thumbnail && assetService.cacheAsset(variant.thumbnail);
            variant.material && assetService.cacheMaterial(variant.material);
        });
    });
    return response.data.options;
};

const findOptionFromPromise = async (
    id: string,
    promise: Promise<OptionResource[]>,
): Promise<OptionResource | undefined> => {
    const options = await promise;
    return options.find((option) => option.id === id);
};

const getOptions = async (ids: string[]): Promise<OptionResource[]> => {
    const existingPromises = ids.map((id) => promiseCache.get({ id }));
    const uncachedIds = ids.filter((_id, index) => existingPromises[index] === undefined);
    if (uncachedIds.length === 0) {
        return Promise.all(existingPromises);
    }
    const getOptionsPromise = getOptionsInternal(uncachedIds);
    const uncachedPromises = uncachedIds.map((id) =>
        promiseCache.set({ id }, findOptionFromPromise(id, getOptionsPromise)),
    );
    const filteredPromises = existingPromises.filter((promise) => promise !== undefined);
    return await Promise.all(filteredPromises.concat(uncachedPromises));
};

class OptionService {
    /**
     * Allows for retrieving an option, returns the option from a cache if possible.
     * @param id The option ID to be retrieved.
     */
    async getOption(id: string): Promise<undefined | OptionResource> {
        const optionResolver = async () => {
            const asset = (await getOptions([id]))[0];
            if (!asset) {
                return undefined;
            }
            return asset;
        };
        const resolver = optionResolver();
        return resolver;
    }

    async getAssetTileImageForVariant(variant: VariantResource): Promise<string> {
        // Image specifically set as thumbnail.
        const thumbnail = variant.thumbnail;
        if (thumbnail) {
            const thumbThumb = thumbnail.versions?.find((v) => v.name === "thumbnail");
            const thumbCdn = thumbnail.fileLink;
            return thumbThumb?.link || thumbCdn!;
        }

        // Fallback to standard asset instead.
        const asset = variant.asset;
        if (asset) {
            const assetThumb = asset.versions?.find((v) => v.name === "thumbnail");
            const assetCdn = asset.fileLink;
            return assetThumb?.link || assetCdn!;
        }

        if (variant.material) {
            return variant.material.id;
        }

        return "";
    }

    getDefaultVariant(option: OptionResource): VariantResource | undefined {
        const variants = option.variants;
        if (!variants) {
            return undefined;
        }
        if (variants.length === 1) {
            return variants[0];
        }
        if (option.defaultVariant === undefined) {
            return undefined;
        }
        return variants.find((variant) => variant.id === option.defaultVariant?.id);
    }

    /**
     * Returns the first variant marked as selected. This is used by most steps.
     */
    getSelectedVariant = (
        option: OptionResource | undefined,
        selectedVariantIds: string[],
    ): VariantResource | undefined => {
        if (!option || selectedVariantIds.length === 0) {
            return undefined;
        }
        return option.variants?.find((variant) => variant.id === selectedVariantIds[0]);
    };
}

const optionService = new OptionService();
export { optionService };
